import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Users } from "lucide-react";
import Portrait from "@/components/Portrait";
import { papi, errText } from "../portalApi";
import { EmptyState, PageHeader, Spinner, inputCls } from "../ui";

export default function Directory() {
  const [people, setPeople] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [q, setQ] = useState("");
  const [department, setDepartment] = useState("");

  const load = useCallback(async () => {
    try {
      const [d, m] = await Promise.all([
        papi.get("/directory", { params: { q, department } }),
        papi.get("/meta"),
      ]);
      setPeople(d.data);
      setDepartments(m.data.departments);
    } catch (err) {
      toast.error(errText(err, "Could not load the directory."));
    }
  }, [q, department]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="directory-page">
      <PageHeader title="Directory" sub="Find colleagues across RCS, The Topper Academy and the Indian School of Music.">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
          <input
            className={`${inputCls} !w-56 pl-10`}
            placeholder="Search name or role"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="directory-search"
            aria-label="Search directory"
          />
        </div>
        <select className={`${inputCls} !w-48`} value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="directory-department-filter" aria-label="Filter by department">
          <option value="">All departments</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </PageHeader>

      {!people ? (
        <Spinner />
      ) : people.length === 0 ? (
        <EmptyState icon={Users} title="No colleagues found" sub="Try a different search or department filter." testid="directory-empty" />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="directory-grid">
          {people.map((p) => (
            <div key={p.id} className="card-premium text-center" data-testid={`directory-card-${p.employeeId || p.id}`}>
              <Portrait name={p.fullName} src={p.photograph || ""} size="md" className="mx-auto" testid={`directory-photo-${p.employeeId || p.id}`} />
              <h3 className="mt-4 font-serif text-lg text-navy-900">{p.fullName}</h3>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gold-dark">{p.designation || "Team Member"}</p>
              <p className="mt-2 text-xs text-slate-500">
                {p.department}{p.brand ? ` · ${p.brand}` : ""}
              </p>
              {p.skills && <p className="mt-3 text-xs italic leading-relaxed text-slate-400">{p.skills}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
